import React, { useState, useEffect } from 'react';
import { useAB } from '@guardian/ab-react';
import { constructQuery } from '@root/src/lib/querystring';
import { incrementUserDismissedGateCount, setUserDismissedGate, } from '@frontend/web/components/SignInGate/dismissGate';
import { getCookie } from '@frontend/web/browser/cookie';
// Sign in Gate A/B Tests
import { signInGateMainVariant } from '@root/src/web/experiments/tests/sign-in-gate-main-variant';
import { signInGateMainControl } from '@root/src/web/experiments/tests/sign-in-gate-main-control';
// Sign in Gate Types
import { signInGateComponent as gateMainVariant } from '@root/src/web/components/SignInGate/gates/main-variant';
import { signInGateComponent as gateMainControl } from '@root/src/web/components/SignInGate/gates/main-control';
import { signInGateComponent as gateMandatoryVariant } from '@root/src/web/components/SignInGate/gates/mandatory-gate';
import { signInGateMandoryTest } from '@root/src/web/experiments/tests/sign-in-gate-mandatory';
import { canShow, canShowMandatoryGate, } from '@root/src/web/components/SignInGate/displayRule';
import { submitViewEventTracking, withComponentId, } from './componentEventTracking';
// component name, should always be sign-in-gate
export const componentName = 'sign-in-gate';
const dismissGate = (setShowGate, currentAbTestValue) => {
    setShowGate(false);
    setUserDismissedGate(currentAbTestValue.variant, currentAbTestValue.name);
    incrementUserDismissedGateCount(currentAbTestValue.variant, currentAbTestValue.name);
};
/* When adding a new test, you need to add the test name to the tests array below,
   and add a entry for each variant that maps it to a SignInGateComponent in testVariantToGateMapping, and in turn match each test id to an component id in testIdToComponentId
*/
const tests = [
    signInGateMainVariant,
    signInGateMainControl,
    signInGateMandoryTest,
];
const testVariantToGateMapping = {
    'main-control-3': gateMainControl,
    'main-variant-3': gateMainVariant(canShow),
    'mandatory-gate-control': gateMainVariant(canShowMandatoryGate),
    'mandatory-gate-variant': gateMandatoryVariant,
};
const testIdToComponentId = {
    SignInGateMainVariant: 'main_variant_3',
    SignInGateMainControl: 'main_control_3',
    SignInGateMandatory: 'mandatory_gate_test',
};
// function to generate the profile.theguardian.com url with tracking params
// and the return url (link to current article page)
const generateSignInUrl = (CAPI, currentTest) => {
    // url of the article, return user here after sign in/registration
    const returnUrl = `${CAPI.config.host}/${CAPI.pageId}`;
    // set the component event params to be included in the query
    const queryParams = {
        componentType: 'signingate',
        componentId: testIdToComponentId[currentTest.id],
        abTestName: currentTest.name,
        abTestVariant: currentTest.variant,
        viewId: window.guardian.ophan.viewId,
        browserId: getCookie('bwid') || undefined,
        visitId: getCookie('vsid') || undefined,
    };
    return `${CAPI.config.idUrl}/signin?returnUrl=${returnUrl}&componentEventParams=${encodeURIComponent(constructQuery(queryParams))}`;
};
// component which shows the sign in gate
// fires a VIEW ophan component event
// and show the gate component if it exists
const ShowSignInGate = ({ CAPI, abTest, setShowGate, signInUrl, gateVariant, }) => {
    // use effect hook to fire view event tracking only on initial render
    useEffect(() => {
        submitViewEventTracking({
            component: withComponentId(testIdToComponentId[abTest.id]),
            abTest,
        });
    }, [abTest]);
    // some sign in gate ab test variants may not need to show a gate
    // therefore the gate is optional
    // this is because we want a section of the audience to never see the gate
    // but still fire a view event if they are eligible to see the gate
    if (gateVariant.gate) {
        return gateVariant.gate({
            guUrl: CAPI.config.host || 'https://theguardian.com/',
            signInUrl,
            dismissGate: () => {
                dismissGate(setShowGate, abTest);
            },
            abTest,
            ophanComponentId: testIdToComponentId[abTest.id],
            isComment: CAPI.designType === 'Comment' ||
                CAPI.designType === 'GuardianView',
        });
    }
    // return nothing if no gate needs to be shown
    return React.createElement(React.Fragment, null);
};
// component with conditional logic which determines if a sign in gate
// should be shown on the current page
export const SignInGateSelector = ({ isSignedIn, CAPI, }) => {
    const [isGateDismissed, setIsGateDismissed] = useState(false);
    const [canShowGate, setCanShowGate] = useState(false);
    const [currentTest, setCurrentTest] = useState({
        name: '',
        variant: '',
        id: '',
    });
    const ab = useAB();
    useEffect(() => {
        const test = ab.firstRunnableTest(tests);
        setCurrentTest({
            name: test?.dataLinkNames || test?.id || '',
            variant: test?.variantToRun.id || '',
            id: test?.id || '',
        });
    }, [ab]);
    useEffect(() => {
        // this hook will fire when the sign in gate is dismissed
        // which will happen when the isGateDismissed state is set to true
        // this only happens within the dismissGate method
        if (isGateDismissed) {
            document.dispatchEvent(new CustomEvent('dcr:page:article:redisplayed'));
        }
    }, [isGateDismissed]);
    // check to see if the test is available on this render cycle
    // required by the ab test framework, as we have to wait for the above
    // useEffect hook to determine which test to run
    if (currentTest.name === '' || currentTest.variant === '') {
        return null;
    }
    const signInUrl = generateSignInUrl(CAPI, currentTest);
    const gateVariant = testVariantToGateMapping?.[currentTest.variant];
    if (gateVariant) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        gateVariant
            .canShow(CAPI, !!isSignedIn, currentTest)
            .then(setCanShowGate);
    }
    return (React.createElement(React.Fragment, null, !isGateDismissed && canShowGate && (React.createElement(ShowSignInGate, { CAPI: CAPI, abTest: currentTest, setShowGate: (show) => setIsGateDismissed(!show), signInUrl: signInUrl, gateVariant: gateVariant }))));
};
